/**
 * Einstellungen abgleichen: der Server ist führend. (23.09.2026, Ticket 2511)
 *
 * Martin: *„Einstellungen sollen zentral und nicht je Gerät gelten. Wer etwas
 * einmal einstellt, erwartet es auf jedem Gerät, genau wie beim Profil."*
 *
 * ## Was vorher schiefging
 *
 * Es gab ZWEI Start-Abgleiche in app.ts, die sich gegenseitig störten. Der
 * erste schrieb zwei Modellfelder in den Speicher, während der zweite noch
 * auf seine Antwort wartete; der zweite hielt das für eine Eingabe des
 * Nutzers, schickte den ganzen alten Gerätestand hoch und übernahm nichts —
 * genau „das Gerät, das zuletzt synchronisiert hat, gewinnt". Dazu: ein
 * fehlgeschlagener Abruf galt als „Server kennt noch nichts", ein PUT prüfte
 * seine Antwort nicht, und abgeglichen wurde nur beim Kaltstart.
 *
 * ## Wie es jetzt läuft
 *
 *   Ändern    settings.ts schreibt den Zwischenspeicher und merkt die Änderung
 *             vor (einstellungen-ausstehend.ts); hier geht sie an den Server.
 *             Die Antwort sagt je Feld: angenommen, überholt (ein anderes
 *             Gerät war jünger) oder verworfen. Ohne Netz bleibt sie
 *             vorgemerkt und wird nachgereicht.
 *   Holen     Nach dem ersten Bild, beim Zurückkehren in die App und wenn das
 *             Netz wiederkommt: erst die Vormerkungen senden, dann alle
 *             Kontowerte vom Server übernehmen. Nie über eine Änderung, die
 *             noch unterwegs ist.
 *   Melden    `voice-v8:settings-sync` sagt jedem, wie es ausging — einmal je
 *             Wechsel, nicht bei jedem stillen Versuch im Hintergrund.
 *
 * ⚠️ Die Darstellung (Helligkeit, Palette, Zuhause-Stil) schreibt hier
 *    NIEMAND selbst — sie geht als Ereignis an appearance.ts, den einzigen
 *    Besitzer dieser Schlüssel.
 */
import {
  ABGELEGTE_FELDER,
  ALTER_MAX_MS,
  DARSTELLUNG_FELDER,
  EINSTELLUNGEN_SCHLUESSEL,
  istGeraeteFeld,
} from "../../shared/einstellungen-felder.js";
import {
  DEFAULT_SETTINGS,
  leseAltesTippenVorlesen,
  liesEinstellungenRoh,
  loadSettings,
  uebernimmVomServer,
} from "./settings.js";
import {
  bindeAn,
  ergebnis,
  istAusstehend,
  liesAusstehend,
  lokalGeaendertSeit,
  merkeAenderungen,
  setzeSender,
  trageAus,
  verwerfeAusstehend,
  type AusstehendesFeld,
  type SpeicherErgebnis,
  type SyncZustand,
} from "./einstellungen-ausstehend.js";

export type { SpeicherErgebnis, SyncZustand };

/** Wem der Zwischenspeicher gehört (nach einem Kontowechsel ≠ angemeldet). */
const BESITZER_SCHLUESSEL = "voice-v8.settings-uid";
/** Die Übernahme alter Gerätestände ist auf diesem Gerät gelaufen. */
const MIGRATION_SCHLUESSEL = "voice-v8.settings-abgleich";
const MIGRATION_MARKE = "2511";
/** Der Gerätestand vor dem Umbau — wird nie automatisch gelöscht. */
const SICHERUNG_SCHLUESSEL = "voice-v8.settings-vor-2511";

/** Beim Zurückkehren in die App höchstens so oft neu holen. */
const HOLEN_HOECHSTENS_MS = 60_000;
const WIEDERHOLEN_AB_MS = 15_000;
const WIEDERHOLEN_BIS_MS = 5 * 60_000;

interface PutAntwort {
  settings?: Record<string, unknown>;
  zeit?: Record<string, unknown>;
  angenommen?: string[];
  ueberholt?: string[];
  verworfen?: string[];
}

interface GetAntwort {
  settings?: Record<string, unknown> | null;
  zeit?: Record<string, unknown>;
}

/** Wie ein PUT ausging — für alle Felder, die er trug. */
interface Rueckmeldung {
  zustand: SyncZustand;
  status?: number;
  gesendet: string[];
  ueberholt: string[];
  verworfen: string[];
}

let aktiveUid: number | null = null;
let laufend: Promise<Rueckmeldung> | null = null;
let naechster: Promise<Rueckmeldung> | null = null;
let holtGerade: Promise<boolean> | null = null;
let anmeldung: Promise<void> | null = null;
let wiederholung: ReturnType<typeof setTimeout> | null = null;
/** Versuche in Folge, die scheiterten (Senden, Holen, Anmelden) — der Takt. */
let fehlversuche = 0;
/** Eine Kontoänderung ist gescheitert und wartet; ihr Gelingen wird gemeldet. */
let nachzureichen = false;
/** Der zuletzt gemeldete Fehlschlag — dieselbe Lage wird nicht erneut gemeldet. */
let gemeldet: string | null = null;
/** Die nächste Sendung trägt eine Eingabe des Nutzers (sendeFuer). */
let vomNutzer = false;
/** Der letzte Abruf ist gescheitert — die Wiederholung holt nach. */
let holenOffen = false;
let letztesHolen = 0;
let hoererDa = false;
/** Wer angemeldet ist, wenn der Start ohne Server lief (app.ts). */
let werBinIch: (() => Promise<number | null>) | null = null;
/**
 * Der Stempel der Serverfassung, auf der der Wert dieses Geräts je Feld
 * beruht: zuletzt selbst geschrieben und bestätigt, oder vom Server
 * übernommen. Geht als `basis` mit — siehe server/einstellungen-zeit.ts.
 */
const basisStempel = new Map<string, number>();

function istObjekt(v: unknown): v is Record<string, unknown> {
  return Boolean(v) && typeof v === "object" && !Array.isArray(v);
}

function gleich(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function lies(schluessel: string): string | null {
  try {
    return localStorage.getItem(schluessel);
  } catch {
    return null;
  }
}

function schreib(schluessel: string, wert: string): void {
  try {
    localStorage.setItem(schluessel, wert);
  } catch {
    /* Privatmodus — dann gilt es eben nur für diese Sitzung */
  }
}

function melde(r: SpeicherErgebnis, nachgereicht = false): void {
  try {
    window.dispatchEvent(
      new CustomEvent("voice-v8:settings-sync", { detail: { ...r, nachgereicht } }),
    );
  } catch {
    /* ohne Fenster (Tests ohne DOM) */
  }
}

/** Nur die Kontowerte — Gerätewerte gelten hier, egal was der Server sagt. */
function kontoFelder(felder: readonly string[]): string[] {
  return felder.filter((f) => !istGeraeteFeld(f));
}

/** Den Stempel eines Felds aus einer Serverantwort merken. */
function merkeBasis(zeit: Record<string, unknown>, feld: string): void {
  const z = zeit[feld];
  if (typeof z === "number" && Number.isFinite(z)) basisStempel.set(feld, z);
  else basisStempel.delete(feld);
}

/**
 * Später noch einmal: senden, holen — oder, wenn beim Start niemand
 * angemeldet werden konnte, erst die Anmeldung. Mit wachsendem Abstand.
 */
function planeWiederholung(): void {
  if (wiederholung) return;
  if (aktiveUid === null && !werBinIch) return;
  const ms = Math.min(
    WIEDERHOLEN_BIS_MS,
    WIEDERHOLEN_AB_MS * 2 ** Math.max(0, fehlversuche - 1),
  );
  wiederholung = setTimeout(() => {
    wiederholung = null;
    void gleicheAb();
  }, ms);
}

/**
 * Ein PUT ist gescheitert. Die Vormerkung bleibt, und es wird wiederholt.
 *
 * ⚠️ Gemeldet wird nur ein WECHSEL — oder eine Eingabe des Nutzers, die
 *    gerade nicht ankam. Vorher meldete jeder stille Versuch im Hintergrund
 *    (15 s, 30 s, 1 min … dann alle 5 min, dazu jede Rückkehr in die App)
 *    erneut „Nicht gespeichert"; im Funkloch wurde daraus eine Serie von
 *    Warnungen. Dass etwas wartet, zeigt die Einstellungsseite dauerhaft
 *    und ruhig (wartendeAenderungen); das Gelingen wird einmal gemeldet.
 */
function fehlschlag(
  zustand: "wartet" | "fehler",
  felder: string[],
  konto: string[],
  ausNutzerhand: boolean,
  status?: number,
): Rueckmeldung {
  fehlversuche++;
  if (konto.length > 0) nachzureichen = true;
  planeWiederholung();
  const lage = status === undefined ? zustand : `${zustand}:${status}`;
  if (konto.length > 0 && (ausNutzerhand || lage !== gemeldet)) {
    gemeldet = lage;
    melde(ergebnis(zustand, konto, { status }));
  }
  return { zustand, status, gesendet: felder, ueberholt: [], verworfen: [] };
}

/**
 * Die Serverwerte der Felder übernehmen, bei denen UNSERE Änderung nicht
 * gegriffen hat — außer der Nutzer hat inzwischen schon wieder geändert.
 */
function nimmServerwerte(
  server: Record<string, unknown>,
  zeit: Record<string, unknown>,
  felder: string[],
): void {
  const werte: Record<string, unknown> = {};
  const weg: string[] = [];
  const darstellung: Record<string, unknown> = {};
  for (const feld of felder) {
    if (istGeraeteFeld(feld) || istAusstehend(feld)) continue;
    merkeBasis(zeit, feld);
    if (DARSTELLUNG_FELDER.has(feld)) {
      if (feld in server) darstellung[feld] = server[feld];
      continue;
    }
    if (feld in server) werte[feld] = server[feld];
    else weg.push(feld);
  }
  uebernimmVomServer(werte, weg);
  gibDarstellungWeiter(darstellung);
}

/** appearance.ts ist der einzige Schreiber dieser Schlüssel (Ticket 2511). */
function gibDarstellungWeiter(werte: Record<string, unknown>): void {
  if (Object.keys(werte).length === 0) return;
  try {
    window.dispatchEvent(
      new CustomEvent("voice-v8:darstellung-vom-server", { detail: werte }),
    );
  } catch {
    /* ohne Fenster */
  }
}

async function sendeJetzt(): Promise<Rueckmeldung> {
  const ausNutzerhand = vomNutzer;
  vomNutzer = false;
  const stand = liesAusstehend();
  const gesendet: Record<string, AusstehendesFeld> = { ...stand.felder };
  const felder = Object.keys(gesendet);
  const leer: Rueckmeldung = {
    zustand: "gespeichert",
    gesendet: [],
    ueberholt: [],
    verworfen: [],
  };
  if (felder.length === 0) return leer;
  /*
   * Gemeldet wird nur, was ein Mensch geändert hat: kein Gerätewert (gilt
   * hier ohnehin) und keine Übernahme alter Gerätestände (die hat niemand
   * angestoßen — ein „Nicht gespeichert" dazu wäre nur verwirrend).
   */
  const echt = (f: string): boolean => gesendet[f]?.nurWennFehlt !== true;
  const konto = kontoFelder(felder.filter(echt));
  /*
   * ⚠️ Erst senden, wenn feststeht, WER angemeldet ist. Eine Vormerkung
   *    eines anderen Kontos (Kontowechsel auf demselben Gerät) geht nie an
   *    dieses — sonst landete z. B. die Klonstimme des Vorgängers im neuen
   *    Konto (das ist vor Ticket 2511 wirklich möglich gewesen).
   */
  if (aktiveUid === null) {
    // Der Start läuft noch: starteAbgleich reicht es gleich nach.
    if (!werBinIch) return { ...leer, zustand: "wartet", gesendet: felder };
    // Der Start lief ohne Server: das ist ein echtes „wartet".
    return fehlschlag("wartet", felder, konto, ausNutzerhand);
  }
  if (stand.uid !== null && stand.uid !== aktiveUid) {
    console.warn("[einstellungen] Vormerkungen eines anderen Kontos verworfen:", felder);
    verwerfeFremdes();
    return leer;
  }
  if (stand.uid === null) bindeAn(aktiveUid);

  const jetzt = Date.now();
  const aenderungen: Record<
    string,
    { wert: unknown; alterMs: number; nurWennFehlt?: true; basis?: number }
  > = {};
  for (const [feld, e] of Object.entries(gesendet)) {
    const basis = e.nurWennFehlt ? undefined : basisStempel.get(feld);
    aenderungen[feld] = {
      wert: e.wert,
      alterMs: Math.max(0, jetzt - e.geaendertAm),
      ...(e.nurWennFehlt ? { nurWennFehlt: true as const } : {}),
      ...(basis !== undefined ? { basis } : {}),
    };
  }
  if (konto.length > 0) melde(ergebnis("speichert", konto));

  let res: Response;
  try {
    res = await fetch("/api/settings", {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ aenderungen }),
    });
  } catch {
    return fehlschlag("wartet", felder, konto, ausNutzerhand);
  }
  if (!res.ok) return fehlschlag("fehler", felder, konto, ausNutzerhand, res.status);
  let antwort: PutAntwort | null;
  try {
    antwort = (await res.json()) as PutAntwort;
  } catch {
    antwort = null;
  }
  /*
   * ⚠️ Ein Server vor Ticket 2511 antwortet auch auf die neue Form mit 200 —
   *    und verwirft sie still. Ohne die Liste `angenommen` gilt deshalb
   *    NICHTS als gespeichert; die Vormerkung bleibt, bis er es versteht.
   */
  if (!antwort || !Array.isArray(antwort.angenommen)) {
    return fehlschlag("fehler", felder, konto, ausNutzerhand, res.status);
  }
  const zeit = istObjekt(antwort.zeit) ? antwort.zeit : {};
  const ueberholt = Array.isArray(antwort.ueberholt) ? antwort.ueberholt : [];
  const verworfen = Array.isArray(antwort.verworfen) ? antwort.verworfen : [];
  for (const feld of antwort.angenommen) merkeBasis(zeit, feld);
  trageAus(gesendet, [...antwort.angenommen, ...ueberholt, ...verworfen]);
  /*
   * Eine Übernahme alter Gerätestände, die der Server nicht braucht (er
   * kennt das Feld schon) oder nicht annimmt, ändert hier nichts: Sie war
   * kein Wunsch des Nutzers. Ihre Werte liegen in der Sicherung.
   */
  nimmServerwerte(
    istObjekt(antwort.settings) ? antwort.settings : {},
    zeit,
    [...ueberholt, ...verworfen].filter(echt),
  );

  const nachgereicht = nachzureichen;
  nachzureichen = false;
  gemeldet = null;
  fehlversuche = 0;
  const kontoUeberholt = kontoFelder(ueberholt.filter(echt));
  const kontoVerworfen = kontoFelder(verworfen.filter(echt));
  let zustand: SyncZustand = "gespeichert";
  if (kontoUeberholt.length > 0) zustand = "ueberholt";
  else if (kontoVerworfen.length > 0) zustand = "fehler";
  if (kontoVerworfen.length > 0) {
    console.warn("[einstellungen] vom Server abgelehnt:", kontoVerworfen);
  }
  if (konto.length > 0) {
    melde(
      zustand === "fehler"
        ? ergebnis("fehler", kontoVerworfen, { abgelehnt: true })
        : ergebnis(zustand, zustand === "ueberholt" ? kontoUeberholt : konto),
      nachgereicht,
    );
  }
  return { zustand, status: res.status, gesendet: felder, ueberholt, verworfen };
}

/**
 * Alle Vormerkungen senden. Es läuft immer nur EIN PUT; was währenddessen
 * dazukommt (ein Regler), geht gesammelt im nächsten.
 */
export function sende(): Promise<Rueckmeldung> {
  if (!laufend) {
    laufend = sendeJetzt().finally(() => {
      laufend = null;
    });
    return laufend;
  }
  naechster ??= laufend.then(() => {
    naechster = null;
    return sende();
  });
  return naechster;
}

/** Senden und die Antwort für genau diese Felder zurückgeben. */
async function sendeFuer(felder: string[]): Promise<SpeicherErgebnis> {
  const konto = kontoFelder(felder);
  // Ein reiner Gerätewert gilt, sobald er hier steht; die Vorlage für neue
  // Geräte geht still mit.
  if (konto.length === 0) {
    void sende();
    return ergebnis("geraet", felder);
  }
  vomNutzer = true;
  const r = await sende();
  const offen = konto.filter((f) => istAusstehend(f));
  if ((r.zustand === "wartet" || r.zustand === "fehler") && offen.length > 0) {
    return ergebnis(r.zustand, offen, { status: r.status });
  }
  const ueberholt = konto.filter((f) => r.ueberholt.includes(f));
  if (ueberholt.length > 0) return ergebnis("ueberholt", ueberholt);
  const verworfen = konto.filter((f) => r.verworfen.includes(f));
  if (verworfen.length > 0) return ergebnis("fehler", verworfen, { abgelehnt: true });
  return ergebnis("gespeichert", konto);
}

/** Die Vormerkungen eines anderen Kontos — sie werden hier nie nachgereicht. */
function verwerfeFremdes(): void {
  verwerfeAusstehend();
  nachzureichen = false;
  gemeldet = null;
}

function liesBesitzer(): number | null {
  const v = Number(lies(BESITZER_SCHLUESSEL));
  return Number.isInteger(v) && v > 0 ? v : null;
}

/**
 * Nach einem Kontowechsel (app.ts, bootstrapAuth) festhalten, wem der
 * Zwischenspeicher gehört — falls der Abgleich das noch nie getan hat.
 */
export function kontoGewechselt(vorher: string | null): void {
  const v = Number(vorher);
  if (!Number.isInteger(v) || v <= 0) return;
  if (liesBesitzer() === null) schreib(BESITZER_SCHLUESSEL, String(v));
}

/** Hat das Gerät für diesen Gerätewert schon einen eigenen? */
function hatEigenenGeraeteWert(feld: string, roh: Record<string, unknown>): boolean {
  if (roh[feld] !== undefined) return true;
  // Ticket 745: der Altschlüssel aus der Zeit vor dem Umzug in die DB.
  return feld === "alwaysRecord" && lies("voice-v8.alwaysRecord") !== null;
}

/** Der Gerätestand vor dem Umbau — einmal, und nie überschrieben. */
function sichereGeraetestand(): void {
  if (lies(SICHERUNG_SCHLUESSEL) !== null) return;
  schreib(
    SICHERUNG_SCHLUESSEL,
    JSON.stringify({
      gesichertAm: new Date().toISOString(),
      settings: lies(EINSTELLUNGEN_SCHLUESSEL),
      tippenVorlesen: lies("voice-v8.tippen-vorlesen"),
      alwaysRecord: lies("voice-v8.alwaysRecord"),
    }),
  );
}

function holenGescheitert(): false {
  holenOffen = true;
  fehlversuche++;
  planeWiederholung();
  return false;
}

/**
 * Den Serverstand holen und übernehmen.
 *
 * Migration — einmal je Gerät: Felder, die der Server nicht kennt, die
 * dieses Gerät aber hat, gehen hoch (nur wenn sie am Server wirklich fehlen).
 * Fremd — der Zwischenspeicher gehört einem anderen Konto: nichts davon geht
 * hoch, und was der Server nicht kennt, fliegt raus.
 *
 * ⚠️ Beides steht in den MARKEN des Speichers, nicht beim Aufrufer. Vorher
 *    galten die Schalter nur für den ersten Abruf nach dem Start. Scheiterte
 *    genau der (Neustart des Servers, Funkloch), holte das nächste Holen ohne
 *    sie — und setzte trotzdem Besitzer und Marke. Beim nächsten Start lud
 *    die Migration dann die Werte des VORGÄNGERS ins neue Konto (seine
 *    Klonstimme), und der alte Gerätestand war ohne Sicherung überschrieben.
 */
async function hole(): Promise<boolean> {
  const uid = aktiveUid;
  if (uid === null) return false;
  const beginn = Date.now();
  let res: Response;
  try {
    res = await fetch("/api/settings", { credentials: "same-origin", cache: "no-store" });
  } catch {
    return holenGescheitert();
  }
  // ⚠️ Ein Fehler ist KEIN „der Server kennt noch nichts" — vorher schob
  //    genau diese Verwechslung nach einem Neustart den Gerätestand hoch.
  if (!res.ok) return holenGescheitert();
  let antwort: GetAntwort;
  try {
    antwort = (await res.json()) as GetAntwort;
  } catch {
    return holenGescheitert();
  }
  holenOffen = false;
  const server = istObjekt(antwort.settings) ? antwort.settings : {};
  const zeit = istObjekt(antwort.zeit) ? antwort.zeit : {};
  const besitzer = liesBesitzer();
  const fremd = besitzer !== null && besitzer !== uid;
  const migration = lies(MIGRATION_SCHLUESSEL) !== MIGRATION_MARKE;
  // Ein fremder Stand gehört nicht in die Sicherung dieses Kontos.
  if (migration && !fremd) sichereGeraetestand();
  // Einmal-Migrationen zuerst (Stimmen-Umzug, Tempo) — sie schreiben zurück;
  // hochgeladen wird nur, was danach im Speicher steht.
  loadSettings();
  const roh = liesEinstellungenRoh();
  const vorgabe = DEFAULT_SETTINGS as unknown as Record<string, unknown>;
  // Nie über eine Änderung, die noch unterwegs ist oder während des Holens kam.
  const frei = (feld: string): boolean =>
    !istAusstehend(feld) && !lokalGeaendertSeit(feld, beginn);

  const uebernehmen: Record<string, unknown> = {};
  const entfernen: string[] = [];
  const hochladen: Record<string, unknown> = {};
  const darstellung: Record<string, unknown> = {};
  const alle = new Set([...Object.keys(server), ...Object.keys(roh), ...Object.keys(vorgabe)]);
  for (const feld of alle) {
    if (ABGELEGTE_FELDER.has(feld) || !frei(feld)) continue;
    if (DARSTELLUNG_FELDER.has(feld)) {
      if (feld in server) {
        darstellung[feld] = server[feld];
        merkeBasis(zeit, feld);
      }
      continue;
    }
    if (istGeraeteFeld(feld)) {
      // Die Vorlage gilt nur für ein Gerät ohne eigenen Wert (Ticket 745).
      if (feld in server && !hatEigenenGeraeteWert(feld, roh)) {
        uebernehmen[feld] = server[feld];
      }
      continue;
    }
    if (feld in server) {
      if (!gleich(server[feld], roh[feld])) {
        if (migration && !fremd && roh[feld] !== undefined) {
          // Nicht still verworfen: Der Gerätewert steht im Protokoll und in
          // der Sicherung (voice-v8.settings-vor-2511).
          console.info(`[einstellungen] 2511 ${feld} lokal→server`, {
            lokal: roh[feld],
            server: server[feld],
          });
        }
        uebernehmen[feld] = server[feld];
      }
      // Hier steht jetzt die Fassung des Servers — eine Änderung baut auf ihr auf.
      merkeBasis(zeit, feld);
    } else if (fremd) {
      if (roh[feld] !== undefined) entfernen.push(feld);
    } else if (migration || feld === "tippenVorlesen") {
      /*
       * ⚠️ 15.10.2026: tippenVorlesen auch NACH der Übernahme. Bis dahin war
       *    die Vorgabe AUS, also ging ein alter Geräteschlüssel "0" nie hoch
       *    (er glich der Vorgabe). Seit die Vorgabe AN ist, wäre dieses Aus
       *    sonst auf jedem anderen Gerät des Kontos verloren. Hochgeladen wird
       *    nur, was von der Vorgabe abweicht — ein „nie gesetzt“ nie, und
       *    nur, solange der Server das Feld nicht kennt.
       */
      const lokal =
        feld === "tippenVorlesen" && roh[feld] === undefined
          ? leseAltesTippenVorlesen()
          : roh[feld];
      /*
       * ⚠️ Nur WIRKLICHE Gerätewerte, keine Vorgaben. Früher schrieb jedes
       *    Speichern das ganze Objekt samt Vorgaben in den Speicher — und
       *    manche Vorgaben weichen vom Server ab (cliChat, heavyModel,
       *    fallbackModel, model). Hochgeladen würden sie das Verhalten des
       *    Servers ändern, obwohl sie nie jemand gewählt hat.
       */
      if (lokal !== undefined && !gleich(lokal, vorgabe[feld])) hochladen[feld] = lokal;
    }
  }

  uebernimmVomServer(uebernehmen, entfernen);
  gibDarstellungWeiter(darstellung);
  schreib(BESITZER_SCHLUESSEL, String(uid));
  if (migration) schreib(MIGRATION_SCHLUESSEL, MIGRATION_MARKE);
  letztesHolen = Date.now();
  if (Object.keys(hochladen).length > 0) {
    console.info("[einstellungen] 2511 vom Gerät übernommen:", Object.keys(hochladen));
    // Als ÄLTESTE mögliche Änderung: jede echte Wahl eines Geräts schlägt sie.
    merkeAenderungen(hochladen, {
      nurWennFehlt: true,
      geaendertAm: Date.now() - ALTER_MAX_MS,
    });
    void sende();
  }
  return true;
}

function holeEinmal(): Promise<boolean> {
  holtGerade ??= hole().finally(() => {
    holtGerade = null;
  });
  return holtGerade;
}

/**
 * Erst die Vormerkungen senden, dann den Serverstand holen — beim
 * Zurückkehren in die App, wenn das Netz wiederkommt, und als Wiederholung
 * nach einem Fehlschlag.
 */
export async function gleicheAb(): Promise<void> {
  if (aktiveUid === null) {
    await holeAnmeldungNach();
    return;
  }
  await sende();
  await holeEinmal();
}

/** Die Anmeldung eines Starts ohne Server nachholen, dann abgleichen. */
function holeAnmeldungNach(): Promise<void> {
  const wer = werBinIch;
  if (!wer) return Promise.resolve();
  anmeldung ??= (async () => {
    let uid: number | null;
    try {
      uid = await wer();
    } catch {
      fehlversuche++;
      planeWiederholung();
      return;
    }
    // Abgemeldet: Es bleibt vorgemerkt, bis sich jemand anmeldet.
    if (uid !== null) await starteAbgleich(uid);
  })().finally(() => {
    anmeldung = null;
  });
  return anmeldung;
}

function richteHoererEin(): void {
  if (hoererDa) return;
  hoererDa = true;
  try {
    // Zurück in der App: was hat sich auf den anderen Geräten getan?
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - letztesHolen < HOLEN_HOECHSTENS_MS && !holenOffen) return;
      void gleicheAb();
    });
    // Das Netz ist zurück: Nachgereichtes zuerst, dann holen.
    window.addEventListener("online", () => {
      void gleicheAb();
    });
  } catch {
    /* ohne DOM */
  }
}

/**
 * Nach dem Anmelden aufrufen (app.ts, bootstrapAuth) — die Oberfläche steht
 * dann schon und wartet NICHT darauf.
 */
export async function starteAbgleich(uid: number): Promise<void> {
  aktiveUid = uid;
  const besitzer = liesBesitzer();
  const fremd = besitzer !== null && besitzer !== uid;
  const stand = liesAusstehend();
  if (fremd || (stand.uid !== null && stand.uid !== uid)) {
    if (Object.keys(stand.felder).length > 0) {
      console.warn("[einstellungen] Vormerkungen eines anderen Kontos verworfen");
    }
    verwerfeFremdes();
  } else if (Object.keys(stand.felder).length > 0) {
    bindeAn(uid);
  }
  richteHoererEin();
  await sende();
  await holeEinmal();
}

/**
 * Der Start lief ohne Server (app.ts, bootstrapAuth: /api/auth/me schlug
 * fehl). (23.09.2026, Ticket 2511)
 *
 * ⚠️ Vorher kehrte bootstrapAuth dann VOR starteAbgleich zurück — und für die
 *    ganze Sitzung wurde nichts gesendet und nichts geholt, auch wenn das
 *    Netz längst wieder da war, während die Oberfläche „Wird nachgereicht"
 *    versprach. Eine iPhone-App, die aus dem Hintergrund zurückkommt, startet
 *    nicht neu; sie lief so tagelang auf dem alten Zwischenspeicher. Jetzt
 *    fragt der Abgleich selbst nach, wer angemeldet ist — wenn das Netz
 *    zurückkommt, beim Zurückkehren in die App und in wachsendem Abstand.
 */
export function starteAbgleichSpaeter(wer: () => Promise<number | null>): void {
  werBinIch = wer;
  richteHoererEin();
  planeWiederholung();
}

setzeSender(sendeFuer);

/**
 * Wartet, bis nichts mehr unterwegs ist (kein PUT, kein Abruf). Für Tests
 * und Diagnose — die Oberfläche wartet nie darauf.
 */
export async function ruhig(): Promise<void> {
  for (let i = 0; i < 20 && (laufend || naechster || holtGerade || anmeldung); i++) {
    await Promise.allSettled([laufend, naechster, holtGerade, anmeldung]);
  }
}

/**
 * Wie viele Kontoänderungen auf den Server WARTEN — gescheitert und
 * vorgemerkt, nicht bloß gerade unterwegs. Die Einstellungsseite zeigt das
 * dauerhaft an, solange es mehr als null sind.
 */
export function wartendeAenderungen(): number {
  if (!nachzureichen) return 0;
  return kontoFelder(Object.keys(liesAusstehend().felder)).length;
}
